import { useEffect, useRef } from 'react'
import MessageBubble from './MessageBubble'
import AnimatedEllipsis from './AnimatedEllipsis'
import type { Candidate, OpenDatesPayload } from '../api'

export interface Message {
  id: string
  role: 'user' | 'assistant' | 'status' | 'candidate' | 'open_dates'
  content: string
  saved?: { target_id: string; target_name: string }[]
  candidate?: Candidate
  openDates?: OpenDatesPayload
}

interface Props {
  messages: Message[]
  loading: boolean
  onApproveCandidate: (messageId: string, itemId: string, label?: string) => Promise<void>
  onSkipCandidate: (messageId: string, itemId: string, label?: string) => Promise<void>
  onDeleteCandidate: (messageId: string, itemId: string) => Promise<void>
}

export default function ChatWindow({ messages, loading, onApproveCandidate, onSkipCandidate, onDeleteCandidate }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 16px 0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {messages.length === 0 && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            textAlign: 'center',
            gap: '8px',
            padding: '32px',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Jontha assistant</div>
          <div style={{ fontSize: '14px', maxWidth: '420px', lineHeight: '1.6' }}>
            Ask me to research artists, venues, or industry contacts — or look up information already in our files.
          </div>
          <div style={{ fontSize: '13px', maxWidth: '420px', lineHeight: '1.6', marginTop: '4px' }}>
            I can also draft emails, subscribe to mailing lists, and filter emails into the research pipeline.
          </div>
        </div>
      )}
      {messages.map((m, i) => (
        <MessageBubble
          key={m.id}
          role={m.role}
          content={m.content}
          saved={m.saved}
          candidate={m.candidate}
          openDates={m.openDates}
          activeStatus={m.role === 'status' && i === messages.length - 1 && loading}
          onApprove={m.candidate ? () => onApproveCandidate(m.id, m.candidate!.item_id, m.candidate!.label) : undefined}
          onSkip={m.candidate ? () => onSkipCandidate(m.id, m.candidate!.item_id, m.candidate!.label) : undefined}
          onDelete={m.candidate ? () => onDeleteCandidate(m.id, m.candidate!.item_id) : undefined}
        />
      ))}
      {loading && (messages.length === 0 || messages[messages.length - 1]?.role === 'user') && (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div
            style={{
              padding: '4px 2px',
              color: 'var(--text-muted)',
              fontSize: '10px',
              letterSpacing: '3px',
            }}
          >
            <AnimatedEllipsis />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}
